/**
 * An item's links, sorted into the groups its page shows. Pure, so jest can
 * test it without the component tree.
 *
 * BOTH DIRECTIONS. What addresses a pain is linked FROM the product TO the
 * pain; a page that listed only links going out of the item never showed it.
 */
import type { BoardroomItemLinkRow } from '../../../types';

/** A heading per link and direction, in the order the page shows them. */
const GROUPS: { link: string; dir: 'out' | 'in'; heading: string }[] = [
  // FIRST, because it says what everything below is about, and it is the way
  // to that tool's own page and all its feedback.
  { link: 'ABOUT', dir: 'in', heading: 'Whose problem it is' },
  // An observation's: everything it was counted over or names.
  { link: 'CITES', dir: 'out', heading: 'What it rests on' },
  { link: 'VERIFIES', dir: 'out', heading: 'Verdicts' },
  { link: 'ADDRESSES', dir: 'in', heading: 'What addresses it' },
  { link: 'CORROBORATES', dir: 'out', heading: 'Others saying it' },
  { link: 'SUPPORTS', dir: 'out', heading: 'Backing it up' },
  { link: 'CRITIQUES', dir: 'out', heading: 'Critiques' },
  { link: 'ADDRESSES', dir: 'out', heading: 'What it addresses' },
  { link: 'ANSWERS', dir: 'out', heading: 'Answers' },
  { link: 'SPAWNED', dir: 'out', heading: 'Found while digging it' },
  { link: 'SPAWNED', dir: 'in', heading: 'Found under' },
];

export interface LinkGroup {
  key: string;
  heading: string;
  rows: BoardroomItemLinkRow[];
}

/** The links in their groups, each group newest first; groups with nothing
 *  in them are left out. A link kind with no heading of its own gets one
 *  made from its name, so nothing linked is ever dropped. */
export function linkGroups(links: BoardroomItemLinkRow[]): LinkGroup[] {
  const byKey = new Map<string, BoardroomItemLinkRow[]>();
  for (const l of links) {
    const key = `${l.dir}:${l.link}`;
    byKey.set(key, [...(byKey.get(key) ?? []), l]);
  }
  const newest = (rows: BoardroomItemLinkRow[]) =>
    [...rows].sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));
  const out: LinkGroup[] = [];
  for (const g of GROUPS) {
    const key = `${g.dir}:${g.link}`;
    const rows = byKey.get(key);
    if (rows?.length) out.push({ key, heading: g.heading, rows: newest(rows) });
    byKey.delete(key);
  }
  for (const [key, rows] of Array.from(byKey.entries())) {
    const [dir, link] = key.split(':');
    const name = link.toLowerCase().replace(/_/g, ' ');
    out.push({ key, heading: dir === 'in' ? `${name} (to it)` : name, rows: newest(rows) });
  }
  return out;
}
